import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, updatePassword, updateProfile, updateEmail } from 'firebase/auth';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { Contract, DEFAULT_TASK_TEMPLATE, DEFAULT_STATUS_OPTIONS } from './types/contract';
import { Company, CompanyMember } from './types/company';
import { CompanyService } from './services/companyService';
import { InvitationService } from './services/invitationService';
import { UserRole } from './types/userProfile';
import { SessionManager } from './utils/sessionManager';
import { SecurityMonitor } from './services/securityMonitor';
import { AuditLogger } from './services/auditLogger';
import { updateTemplateStatusOptions } from './scripts/updateTemplateStatusOptions';
import Login from './components/Login';
import MainDashboard from './components/MainDashboard';
import CompanyManagement from './components/CompanyManagement';
import SettingsModal from './components/SettingsModal';
import AdminPanel from './components/AdminPanel';
import MasterControlPanel from './components/MasterControlPanel';
import SecurityDashboard from './components/SecurityDashboard';
import ToastContainer, { ToastMessage } from './components/ToastContainer';
import './App.css';

type AppState = 'login' | 'dashboard' | 'company-management' | 'admin-panel' | 'master-control' | 'security-dashboard';

function App() {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [appState, setAppState] = useState<AppState>('login');
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [userInfo, setUserInfo] = useState({
    company: 'Trading Company Ltd.',
    userName: 'John Smith',
    userId: 'USR-001'
  });
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [showEmailVerificationBanner, setShowEmailVerificationBanner] = useState(false);
  
  // Company-related state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [userCompanies, setUserCompanies] = useState<Company[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<UserRole>('USER');
  const [currentUserNickname, setCurrentUserNickname] = useState<string>('');

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    const newToast: ToastMessage = {
      id: Date.now(),
      message,
      type
    };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const loadUserRole = async (userId: string, user: any) => {
    try {
      // Always fetch from Firestore to ensure nickname is up-to-date
      const userProfile = await InvitationService.getUserProfile(userId);
      
      if (userProfile) {
        // Check if nickname has old format (3 letters) instead of new format (5 chars: ABC01)
        const hasOldNicknameFormat = userProfile.nickname && userProfile.nickname.length === 3;
        
        if (hasOldNicknameFormat) {
          // Delete old profile and recreate with new format
          console.log('⚠️ Old nickname format detected:', userProfile.nickname, '- recreating profile...');
          console.log('User data:', { userEmail: user?.email, profileEmail: userProfile.email, displayName: user?.displayName });
          
          const { deleteDoc, doc } = await import('firebase/firestore');
          await deleteDoc(doc(db, 'userProfiles', userId));
          
          // Create new profile with correct format - prioritize actual Firebase user email
          const email = user?.email || userProfile.email || 'unknown@example.com';
          console.log('Creating profile with email:', email);
          
          // Get the correct role from invitation code usage history
          const correctRole = await InvitationService.getUserRoleFromUsageHistory(userId) || userProfile.role || 'USER';
          console.log('🔍 DEBUG: Profile recreation - using role:', correctRole, 'for user:', email);
          
          await InvitationService.createUserProfile(userId, email, correctRole, user?.displayName || userProfile.displayName);
          
          // Reload the new profile
          const newProfile = await InvitationService.getUserProfile(userId);
          if (newProfile) {
            setCurrentUserRole(newProfile.role);
            setCurrentUserNickname(newProfile.nickname || '');
            localStorage.setItem('userRole', newProfile.role);
            localStorage.setItem('userNickname', newProfile.nickname || '');
            console.log('✅ Profile recreated with new nickname:', newProfile.nickname, 'for email:', email);
            return newProfile.role;
          }
        } else {
          // Check if profile is missing nickname (existing user migration)
          if (!userProfile.nickname) {
            console.log('⚠️ Profile missing nickname - generating one...');
            const { updateDoc, doc } = await import('firebase/firestore');
            const { NicknameService } = await import('./services/nicknameService');
            
            // Generate nickname
            const email = user?.email || userProfile.email || 'unknown@example.com';
            const displayName = user?.displayName || userProfile.displayName || email.split('@')[0];
            const newNickname = await NicknameService.generateUniqueNickname(displayName, email);
            
            // Update Firestore
            await updateDoc(doc(db, 'userProfiles', userId), {
              nickname: newNickname
            });
            
            console.log('✅ Generated nickname for existing user:', newNickname);
            
            setCurrentUserRole(userProfile.role);
            setCurrentUserNickname(newNickname);
            localStorage.setItem('userRole', userProfile.role);
            localStorage.setItem('userNickname', newNickname);
            return userProfile.role;
          }
          
          // Use existing profile with correct format
          console.log('✅ Using existing profile:', { nickname: userProfile.nickname, role: userProfile.role, email: userProfile.email });
          setCurrentUserRole(userProfile.role);
          setCurrentUserNickname(userProfile.nickname || '');
          localStorage.setItem('userRole', userProfile.role);
          localStorage.setItem('userNickname', userProfile.nickname || '');
          return userProfile.role;
        }
      }

      // No profile found - create one with generated nickname using the actual Firebase user
      const email = user?.email || 'unknown@example.com';
      const nickname = email.split('@')[0].substring(0, 3).toUpperCase();
      
      // Check if user has an existing profile with role from invitation code
      const existingProfile = await InvitationService.getUserProfile(userId);
      
      if (existingProfile) {
        // Use existing profile role - don't recreate
        setCurrentUserRole(existingProfile.role);
        setCurrentUserNickname(existingProfile.nickname || '');
        localStorage.setItem('userRole', existingProfile.role);
        localStorage.setItem('userNickname', existingProfile.nickname || '');
        console.log('✅ Using existing profile:', email, 'with nickname:', existingProfile.nickname, 'and role:', existingProfile.role);
        return existingProfile.role;
      }
      
      // Only create new profile if none exists - check invitation code usage history first
      const userRole = await InvitationService.getUserRoleFromUsageHistory(userId) || 'USER';
      console.log('🔍 DEBUG: No existing profile - using role from usage history:', userRole, 'for user:', email);
      
      // Create user profile with correct role (preserve invitation code role)
      await InvitationService.createUserProfile(userId, email, userRole, user?.displayName);
      
      // Set the values
      setCurrentUserRole(userRole);
      setCurrentUserNickname(nickname);
      localStorage.setItem('userRole', userRole);
      localStorage.setItem('userNickname', nickname);
      
      console.log('✅ Created profile for:', email, 'with nickname:', nickname, 'and role:', userRole);
      
      return userRole;
    } catch (error) {
      console.error('Error loading user role:', error);
      setCurrentUserRole('USER');
      return 'USER';
    }
  };

  useEffect(() => {
    // Initialize Security Monitor
    SecurityMonitor.initialize();
    
    // Add global error handler to track ALL authentication failures
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Check if this is a Firebase Auth error
      const errorMessage = args.join(' ');
      if (errorMessage.includes('auth/invalid-credential') || 
          errorMessage.includes('auth/user-not-found') || 
          errorMessage.includes('auth/wrong-password')) {
        console.log('🔒 Detected Firebase Auth error in console:', errorMessage);
        // Try to extract email from the error message
        const emailMatch = errorMessage.match(/email[:\s]+([^\s,]+)/i);
        const email = emailMatch ? emailMatch[1] : 'unknown';
        console.log('🔒 Tracking login failure for:', email);
        SecurityMonitor.monitorLoginAttempt(email, false);
      }
      originalConsoleError.apply(console, args);
    };
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      // Monitor authentication state changes for security
      if (user) {
        // Successful authentication
        console.log('🔒 Authentication success:', user.email);
        await SecurityMonitor.monitorLoginAttempt(user.email || 'unknown', true, user.uid);
        
        // Check email verification status
        if (!user.emailVerified) {
          console.log('⚠️ Email not verified for:', user.email);
          setShowEmailVerificationBanner(true);
        } else {
          setShowEmailVerificationBanner(false);
        }
      } else {
        // Authentication failure or logout
        console.log('🔒 Authentication state changed: user logged out or failed');
        setShowEmailVerificationBanner(false);
      }
      
      if (user) {
        setAppState('dashboard');
        
        // Start session management
        SessionManager.startSession(
          handleSessionTimeout,
          handleSessionWarning
        );
        
        // Load user role first (pass the user object)
        const userRole = await loadUserRole(user.uid, user);
        console.log('🔍 DEBUG: loadUserRole returned:', userRole);
        console.log('🔍 DEBUG: currentUserRole state:', currentUserRole);
        
        // Load user companies and set up company context
        try {
          // MASTER accounts should see ALL companies, not just their member companies
          // Use the returned role directly instead of state to avoid timing issues
          const companies = userRole === 'MASTER' 
            ? await CompanyService.getAllCompanies()
            : await CompanyService.getUserCompanies(user.uid);
            
          console.log('🔍 DEBUG: Loading companies for role:', userRole);
          console.log('🔍 DEBUG: Companies loaded:', companies.length);
            
          setUserCompanies(companies);
          
          if (companies.length > 0) {
            setSelectedCompany(companies[0]);
            loadContracts(user.uid, companies[0].id);
            
            // Update user info with company data
            setUserInfo(prev => ({
              ...prev,
              company: companies[0].name,
              userName: user.displayName || user.email || 'User',
              userId: user.uid
            }));
          } else {
            // No companies - stay on dashboard but show company management option
            setSelectedCompany(null);
            setUserInfo(prev => ({
              ...prev,
              company: 'No Company Selected',
              userName: user.displayName || user.email || 'User',
              userId: user.uid
            }));
          }
        } catch (error) {
          console.error('Error loading user companies:', error);
          showToast('Error loading companies', 'error');
        }
      } else {
        setAppState('login');
        setContracts([]);
        setUserCompanies([]);
        setSelectedCompany(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadContracts = (userId: string, companyId?: string) => {
    try {
      let q;
      
      if (companyId) {
        // Load contracts for specific company
        q = query(
          collection(db, 'contracts'),
          where('companyId', '==', companyId),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Fallback to user-based loading (for backward compatibility)
        q = query(
          collection(db, 'contracts'),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
      }

      const unsubscribe = onSnapshot(
        q, 
        (snapshot) => {
          console.log('📥 onSnapshot triggered - Loaded contracts:', snapshot.docs.length);
          const contractsData = snapshot.docs.map(doc => {
            const data = doc.data();
            console.log('📄 Contract:', doc.id, data.contractNumber);
            
            // Ensure all tasks have updated customStatusOptions with Yes/No
            const updatedTasks = data.tasks?.map((task: any) => ({
              ...task,
              customStatusOptions: task.customStatusOptions || DEFAULT_STATUS_OPTIONS
            })) || [];
            
            return {
              id: doc.id,
              ...data,
              tasks: updatedTasks
            };
          }) as Contract[];
          console.log('📋 Setting contracts state with', contractsData.length, 'contracts');
          setContracts(contractsData);
        },
        (error: any) => {
          // Ignore permission errors when user is logged out
          if (error.code === 'permission-denied') {
            console.log('Permission denied - user likely logged out');
            return;
          }
          
          console.error('Error loading contracts:', error);
          console.error('Error code:', error.code);
          console.error('Error message:', error.message);
          
          if (error.code === 'failed-precondition' || error.message.includes('index')) {
            console.error('⚠️ FIRESTORE INDEX REQUIRED!');
            console.error('The error message above contains a link to create the index.');
            console.error('Click the link to automatically create the required index.');
            
            showToast('Firestore Index Required! Check console for link.', 'error');
          } else {
            showToast('Error loading contracts: ' + error.message, 'error');
          }
        }
      );

      return unsubscribe;
    } catch (error: any) {
      console.error('Error setting up contract listener:', error);
      showToast('Error setting up real-time sync: ' + error.message, 'error');
      return () => {}; // Return empty function
    }
  };

  const handleLogin = async (user: any) => {
    setCurrentUser(user);
    
    // Load user companies and set up company context
    try {
      // Get the role from localStorage to avoid timing issues
      const userRole = localStorage.getItem('userRole') || 'USER';
      console.log('🔍 DEBUG: handleLogin - using role from localStorage:', userRole);
      
      // MASTER accounts should see ALL companies, not just their member companies
      const companies = userRole === 'MASTER' 
        ? await CompanyService.getAllCompanies()
        : await CompanyService.getUserCompanies(user.uid);
        
      setUserCompanies(companies);
      
      if (companies.length > 0) {
        setSelectedCompany(companies[0]);
        setAppState('dashboard');
        loadContracts(user.uid, companies[0].id);
        
        // Update user info with company data
        setUserInfo(prev => ({
          ...prev,
          company: companies[0].name,
          userName: user.displayName || user.email || 'User',
          userId: user.uid
        }));
      } else {
        // No companies - show company management to create one
        setAppState('company-management');
      }
    } catch (error) {
      console.error('Error loading user companies:', error);
      showToast('Error loading companies', 'error');
    }
  };

  const handleCompanySelect = (companyId: string) => {
    console.log('🔍 DEBUG: handleCompanySelect called with companyId:', companyId);
    console.log('🔍 DEBUG: userCompanies:', userCompanies);
    
    const company = userCompanies.find(c => c.id === companyId);
    console.log('🔍 DEBUG: found company:', company);
    
    if (company) {
      console.log('🔍 DEBUG: Setting selected company to:', company.name);
      setSelectedCompany(company);
      setUserInfo(prev => ({
        ...prev,
        company: company.name
      }));
      loadContracts(currentUser.uid, companyId);
      setAppState('dashboard');
      console.log('🔍 DEBUG: Company selection completed');
    } else {
      console.error('🔍 DEBUG: Company not found with ID:', companyId);
    }
  };

  const handleShowCompanyManagement = () => {
    setAppState('company-management');
  };

  const handleShowAdminPanel = () => {
    setAppState('admin-panel');
  };

  const handleShowMasterControl = () => {
    setAppState('master-control');
  };

  const handleShowSecurityDashboard = () => {
    setAppState('security-dashboard');
  };

  const handleBackToDashboard = () => {
    setAppState('dashboard');
  };

  const handleLogout = async () => {
    try {
      // Stop session management
      SessionManager.stopSession();
      
      await signOut(auth);
      setCurrentUser(null);
      setAppState('login');
      setContracts([]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSessionTimeout = async () => {
    // Log session timeout
    if (currentUser) {
      await SecurityMonitor.monitorSessionTimeout(currentUser.uid, currentUser.email);
    }
    
    // Show timeout message
    showToast('Session expired due to inactivity. Please log in again.', 'warning');
    
    // Logout user
    await handleLogout();
  };

  const handleSessionWarning = () => {
    // Show warning message
    showToast('Your session will expire in 10 minutes due to inactivity.', 'info');
  };

  const handleUserInfoChange = (field: string, value: string) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAddContract = async () => {
    if (!currentUser) {
      alert('You must be logged in to create a contract.');
      return;
    }
    
    if (!selectedCompany) {
      const createCompany = window.confirm(
        'You need to create a company first to manage contracts.\n\n' +
        'Would you like to go to Company Management to create one?'
      );
      if (createCompany) {
        setAppState('company-management');
      }
      return;
    }

    try {
      console.log('Creating contract for user:', currentUser.uid, 'company:', selectedCompany.id);
      
      // Prompt for contract details
      const contractNumber = prompt('Enter contract number:', `CONTRACT-${String(contracts.length + 1).padStart(3, '0')}`);
      if (!contractNumber) return;
      
      const product = prompt('Enter product/commodity name:', '');
      if (!product) return;
      
      const yearInput = prompt('Enter year:', new Date().getFullYear().toString());
      if (!yearInput) return;
      
      const year = parseInt(yearInput) || new Date().getFullYear();
      
      // Reset all task statuses to 'Pending' for Default Template
      const defaultTasks = DEFAULT_TASK_TEMPLATE.map(task => ({
        ...task,
        status: 'Pending' // Reset all statuses to 'Pending'
      }));
      
      // Debug: Verify all statuses are 'Pending'
      const nonPendingTasks = defaultTasks.filter(task => task.status !== 'Pending');
      if (nonPendingTasks.length > 0) {
        console.warn('⚠️ Warning: Some tasks do not have Pending status:', nonPendingTasks);
      } else {
        console.log('✅ All task statuses reset to Pending');
      }
      
      const newContract: Omit<Contract, 'id'> = {
        contractNumber: contractNumber,
        product: product,
        transportationVehicle: 'Vessel',
        year: year,
        status: 'Pending',
        progress: 0,
        tasks: defaultTasks,
        createdAt: new Date().toISOString(),
        userId: currentUser.uid,
        companyId: selectedCompany.id,
        createdBy: currentUserNickname || (currentUser.displayName ? 
          currentUser.displayName.split(' ').map((n: string) => n[0]).join('').substring(0, 3).toUpperCase() :
          currentUser.email?.substring(0, 3).toUpperCase()) || 'USR',
        createdByName: currentUser.displayName || currentUser.email || 'Unknown User',
        createdByUserId: currentUser.uid
      };

      console.log('Contract data:', newContract);
      
      const docRef = await addDoc(collection(db, 'contracts'), newContract);
      console.log('Contract created successfully with ID:', docRef.id);
      showToast('Contract created successfully!', 'success');
    } catch (error: any) {
      console.error('Error adding contract:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Error creating contract. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Firebase Firestore not configured properly. Please:\n\n' +
                       '1. Create Firestore database in Firebase Console\n' +
                       '2. Set up security rules\n\n' +
                       'See FIREBASE_SETUP_GUIDE.md for details.';
      } else if (error.code === 'failed-precondition') {
        errorMessage += 'Firestore index required. Click the link in the browser console to create it.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleSaveContract = async (contract: Contract) => {
    if (!currentUser) return;
    
    try {
      // Check if this is a new contract (temporary ID) or existing contract
      const isNewContract = contract.id.startsWith('temp_');
      
      if (isNewContract) {
        console.log('💾 Creating new contract from template');
        console.log('📋 Contract data:', contract);
        console.log('📋 Number of tasks:', contract.tasks.length);
        console.log('📋 Sample task:', contract.tasks[0]);
        
        // Create new contract document (excluding the temporary ID)
        const { id, ...contractDataWithoutId } = contract;
        const contractData = {
          ...contractDataWithoutId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: currentUser.uid,
          companyId: selectedCompany?.id || ''
        };
        
        console.log('📤 Sending to Firestore:', contractData);
        const docRef = await addDoc(collection(db, 'contracts'), contractData);
        console.log('✅ New contract created with ID:', docRef.id);
        
        // Don't update local state - the real-time listener will handle it
        
        showToast('Contract created successfully!', 'success');
      } else {
        console.log('💾 Updating existing contract:', contract.id);
        // Update existing contract
        const contractRef = doc(db, 'contracts', contract.id);
        await updateDoc(contractRef, {
          ...contract,
          updatedAt: new Date().toISOString()
        });
        
        // Don't update local state - the real-time listener will handle it
        
        console.log('✅ Contract updated successfully');
        showToast('Contract updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      alert('Error saving contract. Please try again.');
    }
  };

  const handleDeleteContract = async (contractId: string, skipConfirmation: boolean = false) => {
    if (!currentUser) return;
    
    const contractToDelete = contracts.find(c => c.id === contractId);
    const contractNumber = contractToDelete?.contractNumber || 'this contract';
    
    // Only show confirmation if not skipped (for bulk operations)
    if (!skipConfirmation) {
      const confirmed = window.confirm(
        `⚠️ WARNING: You are about to delete "${contractNumber}"\n\n` +
        `This action cannot be undone!\n\n` +
        `Click OK to delete, or Cancel to abort.`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    try {
      console.log('Deleting contract:', contractId);
      const contractRef = doc(db, 'contracts', contractId);
      await deleteDoc(contractRef);
      console.log('Contract deleted from Firestore successfully:', contractNumber);
      
      // Only show toast if not bulk delete
      if (!skipConfirmation) {
        showToast(`Contract "${contractNumber}" deleted!`, 'success');
      }
    } catch (error: any) {
      console.error('Error deleting contract:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        showToast('Permission denied! Check Firestore security rules.', 'error');
      } else {
        showToast('Error deleting contract: ' + error.message, 'error');
      }
    }
  };

  const handleDuplicateContract = async (contract: Contract) => {
    if (!currentUser) return;

    try {
      // Explicitly exclude 'id' field to prevent selection bugs
      const { id, ...contractWithoutId } = contract;
      
      // Reset all tasks to clean state (like a fresh template)
      // Keep: task names and customStatusOptions
      // Reset: status, data, comments, and all audit trail fields
      const resetTasks = contract.tasks?.map(task => ({
        name: task.name, // Keep task name
        status: 'Pending', // Reset to default status
        data: '', // Clear data field
        comments: '', // Clear comments
        customStatusOptions: task.customStatusOptions // Keep custom status options if present
        // All audit trail fields are excluded (dataLastEditedBy, dataLastEditedAt, statusLastEditedBy, statusLastEditedAt)
      })) || [];

      const duplicatedContract: Omit<Contract, 'id'> = {
        ...contractWithoutId,
        contractNumber: `${contract.contractNumber}-COPY`, // User will edit this
        product: contract.product, // User will edit this
        transportationVehicle: contract.transportationVehicle, // User will edit this
        year: contract.year, // User will edit this
        status: 'Pending', // Reset contract status
        progress: 0, // Reset progress
        tasks: resetTasks, // Use reset tasks
        createdAt: new Date().toISOString(), // New creation timestamp
        userId: currentUser.uid,
        createdBy: currentUserNickname || (currentUser.displayName ? 
          currentUser.displayName.split(' ').map((n: string) => n[0]).join('').substring(0, 3).toUpperCase() :
          currentUser.email?.substring(0, 3).toUpperCase()) || 'USR',
        createdByName: currentUser.displayName || currentUser.email || 'Unknown User',
        createdByUserId: currentUser.uid
        // updatedAt is excluded (will be undefined, which is correct)
      };

      await addDoc(collection(db, 'contracts'), duplicatedContract);
      showToast('Contract duplicated successfully!', 'success');
    } catch (error) {
      console.error('Error duplicating contract:', error);
      showToast('Error duplicating contract. Please try again.', 'error');
    }
  };

  const handleShowSettings = () => {
    setShowSettings(true);
  };

  const handleCloseSettings = () => {
    setShowSettings(false);
  };

  const handleUsernameChange = async (newUsername: string) => {
    if (!currentUser) return;
    
    try {
      await updateProfile(currentUser, {
        displayName: newUsername
      });
      showToast('Username updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating username:', error);
      throw error;
    }
  };

  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) return;
    
    try {
      await updatePassword(currentUser, newPassword);
      showToast('Password updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  };

  const handleEmailChange = async (newEmail: string, confirmPassword: string) => {
    if (!currentUser) return;
    
    try {
      // First, re-authenticate the user with their current password
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, currentUser.email!, confirmPassword);
      
      // Update the email
      await updateEmail(currentUser, newEmail);
      
      // Send email verification to the new email
      const { EmailVerificationService } = await import('./services/emailVerificationService');
      await EmailVerificationService.sendVerification(currentUser);
      
      showToast('Email updated successfully! Please check your new email for verification.', 'success');
      
      // Log the email change for security audit
      const { AuditLogger } = await import('./services/auditLogger');
      await AuditLogger.logEvent(
        currentUser.uid,
        currentUser.email || 'unknown',
        'EMAIL_CHANGE',
        'user_profile',
        {
          oldEmail: currentUser.email,
          newEmail: newEmail
        },
        'HIGH',
        true
      );
      
    } catch (error: any) {
      console.error('Error updating email:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email address is already in use by another account.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Please enter a valid email address.');
      } else {
        throw new Error('Failed to update email. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Trade Execution Platform...</p>
      </div>
    );
  }

  const handleResendVerification = async () => {
    if (!currentUser) return;
    
    try {
      const { EmailVerificationService } = await import('./services/emailVerificationService');
      await EmailVerificationService.resendVerification(currentUser);
      showToast('Verification email sent! Please check your inbox (including spam folder).', 'success');
    } catch (error: any) {
      console.error('Verification email error:', error);
      showToast(error.message || 'Failed to send verification email. You may have requested too many emails.', 'error');
    }
  };

  const handleSkipVerification = () => {
    // Only allow MASTER users to skip verification
    if (currentUserRole === 'MASTER') {
      setShowEmailVerificationBanner(false);
      showToast('Email verification skipped for MASTER account', 'info');
    }
  };

  const handleCheckVerification = async () => {
    if (!currentUser) return;
    
    try {
      const { EmailVerificationService } = await import('./services/emailVerificationService');
      await currentUser.reload();
      
      if (currentUser.emailVerified) {
        setShowEmailVerificationBanner(false);
        showToast('Email verified successfully!', 'success');
      } else {
        showToast('Email not verified yet. Please check your inbox.', 'warning');
      }
    } catch (error) {
      showToast('Error checking verification status', 'error');
    }
  };

  return (
    <div className="App">
      {/* Email Verification Banner */}
      {showEmailVerificationBanner && currentUser && (
        <div className="email-verification-banner">
          <div className="banner-content">
            <span className="banner-icon">📧</span>
            <span className="banner-message">
              Please verify your email address ({currentUser.email}) to access all features.
              Check your inbox and spam folder.
            </span>
            <div className="banner-actions">
              <button className="btn-resend" onClick={handleResendVerification}>
                Resend Email
              </button>
              <button className="btn-check" onClick={handleCheckVerification}>
                I've Verified
              </button>
              {currentUserRole === 'MASTER' && (
                <button className="btn-skip" onClick={handleSkipVerification} title="Skip verification (MASTER only)">
                  Skip (MASTER)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {appState === 'login' && (
        <Login onLogin={handleLogin} />
      )}
      
      {appState === 'company-management' && currentUser && (
        <div className="company-management-container">
          <div className="company-header">
            <h1>Company Management</h1>
            <div className="header-actions">
              {currentUserRole === 'MASTER' && (
                <>
                  <button 
                    className="btn-secondary"
                    onClick={handleShowMasterControl}
                    title="Master Control Panel"
                  >
                    🏢 Master Control
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={handleShowAdminPanel}
                    title="Manage Invitation Codes"
                  >
                    🔑 Invitation Codes
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={handleShowSecurityDashboard}
                    title="Security Dashboard"
                  >
                    🔒 Security Dashboard
                  </button>
                </>
              )}
              <button 
                className="btn-secondary"
                onClick={handleBackToDashboard}
              >
                ← Back to Dashboard
              </button>
            </div>
          </div>
          <CompanyManagement
            currentUser={currentUser}
            currentUserRole={currentUserRole}
            onCompanySelect={handleCompanySelect}
            showToast={showToast}
          />
        </div>
      )}
      
      {appState === 'master-control' && currentUser && currentUserRole === 'MASTER' && (
        <MasterControlPanel
          currentUser={currentUser}
          showToast={showToast}
          onClose={handleBackToDashboard}
        />
      )}
      
      {appState === 'security-dashboard' && currentUser && currentUserRole === 'MASTER' && (
        <SecurityDashboard
          currentUser={currentUser}
          onClose={handleBackToDashboard}
        />
      )}
      
      {appState === 'admin-panel' && currentUser && currentUserRole === 'MASTER' && (
        <AdminPanel
          currentUser={currentUser}
          showToast={showToast}
          onClose={handleBackToDashboard}
        />
      )}
      
      {appState === 'dashboard' && currentUser && (
        <>
          <MainDashboard
            contracts={contracts}
            onAddContract={handleAddContract}
            onSaveContract={handleSaveContract}
            onDeleteContract={handleDeleteContract}
            onDuplicateContract={handleDuplicateContract}
            onLogout={handleLogout}
            onShowSettings={handleShowSettings}
            onShowCompanyManagement={handleShowCompanyManagement}
            userInfo={userInfo}
            onUserInfoChange={handleUserInfoChange}
            showToast={showToast}
            selectedCompany={selectedCompany}
            userCompanies={userCompanies}
            currentUserNickname={currentUserNickname}
            currentUser={currentUser}
            currentUserRole={currentUserRole}
          />
          
          <SettingsModal
            isOpen={showSettings}
            onClose={handleCloseSettings}
            onUsernameChange={handleUsernameChange}
            onPasswordChange={handlePasswordChange}
            onEmailChange={handleEmailChange}
            currentUsername={currentUser?.displayName || currentUser?.email || 'User'}
            currentEmail={currentUser?.email}
            isEmailVerified={currentUser?.emailVerified}
            userRole={currentUserRole}
          />
        </>
      )}
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

export default App;
